import { useState, useEffect, useMemo } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Fade, TextField, InputAdornment, Button, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '../../common/Pagination';
import LoadingOverlay from '../../common/LoadingOverlay';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function PettyCashReport() {
  useSessionValidation();

  const { companyId, branchId, token, companyName, username } = useAuth();

  const today = new Date().toISOString().split('T')[0];
  const nowD = new Date();
  const firstOfMonth = `${nowD.getFullYear()}-${String(nowD.getMonth() + 1).padStart(2, '0')}-01`;

  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => { setMounted(true); }, []);

  const fetchPettyCashReport = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const response = await fetchData(
        'ReportApi',
        `/PettyCashReport/${companyId}/${branchId || 0}/${fromDate}/${toDate}`,
        token
      );
      const rows = response?.response_data || response || [];
      setData(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Error fetching petty cash report:', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => { setCurrentPage(1); fetchPettyCashReport(); };

  const fmt = (n) =>
    parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const filteredData = useMemo(() =>
    data.filter(item =>
      !searchTerm ||
      String(item.BillNo ?? item.billNo ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.Particulars || item.particulars || item.description1 || '').toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [data, searchTerm]
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Compute running balance over full filtered data
  const balances = useMemo(() => {
    let bal = 0;
    return filteredData.map(item => {
      const particulars = item.Particulars || item.particulars || item.description1 || '';
      if (particulars === 'Closing Balance') return null;
      const receipt = parseFloat(item.Receipt ?? item.receipt ?? item.debit ?? 0);
      const payment = parseFloat(item.Payment ?? item.payment ?? item.credit ?? 0);
      bal += receipt - payment;
      return bal;
    });
  }, [filteredData]);

  // Excel Export
  const handleExportExcel = async () => {
    if (filteredData.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Petty Cash Book');
    worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }];

    const thinBorder = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };

    // Company Name Row
    worksheet.mergeCells('A1:F1');
    const companyCell = worksheet.getCell('A1');
    companyCell.value = companyName || 'Company Name';
    companyCell.font = { bold: true, size: 14, color: { argb: 'FF1F4788' } };
    companyCell.alignment = { vertical: 'middle', horizontal: 'center' };
    companyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6EAF8' } };

    // Title row
    worksheet.mergeCells('A2:F2');
    const titleCell = worksheet.getCell('A2');
    titleCell.value = `Petty Cash Book - From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`;
    titleCell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0f3460' } };

    // Generated By row
    worksheet.mergeCells('A3:F3');
    const generatedCell = worksheet.getCell('A3');
    generatedCell.value = `Generated By: ${username || 'User'} | ${new Date().toLocaleString('en-IN')}`;
    generatedCell.font = { size: 9, color: { argb: 'FF555555' } };
    generatedCell.alignment = { vertical: 'middle', horizontal: 'left' };
    generatedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };

    // Header row
    const headers = ['Bill Date', 'Bill No', 'Particulars', 'Receipt', 'Payment', 'Balance'];
    const headerRow = worksheet.addRow(headers);
    headerRow.height = 22;

    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1a1a2e' } };
      cell.border = thinBorder;
      if (colNumber >= 4) {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      } else {
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      }
    });

    // Data rows with running balance
    let bal = 0;
    filteredData.forEach((item, index) => {
      const particulars = item.Particulars || item.particulars || item.description1 || '';
      const receipt = parseFloat(item.Receipt ?? item.receipt ?? item.debit ?? 0);
      const payment = parseFloat(item.Payment ?? item.payment ?? item.credit ?? 0);

      let displayBalance = '';
      if (particulars !== 'Closing Balance') {
        bal += receipt - payment;
        displayBalance = `${Math.abs(bal).toFixed(2)} ${bal < 0 ? 'Cr' : 'Dr'}`;
      }

      const row = worksheet.addRow([
        formatDate(item.BillDate || item.billDate || item.date1),
        item.BillNo ?? item.billNo ?? item.Estimateno ?? '',
        particulars,
        receipt > 0 ? receipt.toFixed(2) : '',
        payment > 0 ? payment.toFixed(2) : '',
        displayBalance
      ]);

      const isOpening = particulars === 'Opening Balance';
      const isClosing = particulars === 'Closing Balance';

      row.eachCell((cell, colNumber) => {
        cell.font = { size: 10, bold: isOpening || isClosing };
        cell.border = thinBorder;

        if (isOpening) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8f4fd' } };
          cell.font = { size: 10, bold: true, color: { argb: 'FF1565c0' } };
        } else if (isClosing) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8f5e9' } };
          cell.font = { size: 10, bold: true, color: { argb: 'FF2e7d32' } };
        } else {
          const bgColor = index % 2 === 0 ? 'FFFFFFFF' : 'FFF9FAFB';
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        }

        if (colNumber >= 4) {
          cell.alignment = { vertical: 'middle', horizontal: 'right' };
          if (colNumber === 4 || colNumber === 5) {
            cell.numFmt = '#,##0.00';
          }
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        }
      });
    });

    // Closing Balance / Totals row
    const totalReceipt = filteredData.reduce((sum, item) => {
      const p = item.Particulars || item.particulars || item.description1 || '';
      if (p === 'Opening Balance' || p === 'Closing Balance') return sum;
      return sum + parseFloat(item.Receipt ?? item.receipt ?? item.debit ?? 0);
    }, 0);
    const totalPayment = filteredData.reduce((sum, item) => {
      const p = item.Particulars || item.particulars || item.description1 || '';
      if (p === 'Opening Balance' || p === 'Closing Balance') return sum;
      return sum + parseFloat(item.Payment ?? item.payment ?? item.credit ?? 0);
    }, 0);
    const closingBal = bal;

    const totalRow = worksheet.addRow([
      formatDate(toDate),
      '',
      'Closing Balance',
      totalReceipt > 0 ? totalReceipt.toFixed(2) : '',
      totalPayment > 0 ? totalPayment.toFixed(2) : '',
      `${Math.abs(closingBal).toFixed(2)} ${closingBal < 0 ? 'Cr' : 'Dr'}`
    ]);

    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 10, color: { argb: 'FF2e7d32' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8f5e9' } };
      cell.border = {
        top: { style: 'medium', color: { argb: 'FF2e7d32' } },
        bottom: { style: 'medium', color: { argb: 'FF2e7d32' } },
        left: { style: 'thin' },
        right: { style: 'thin' }
      };
      if (colNumber >= 4) {
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      } else {
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      }
    });

    // Set column widths
    worksheet.columns = [
      { width: 14 },  // Bill Date
      { width: 12 },  // Bill No
      { width: 35 },  // Particulars
      { width: 16 },  // Receipt
      { width: 16 },  // Payment
      { width: 20 }   // Balance
    ];

    const fileName = `Petty_Cash_Book_${formatDate(fromDate).replace(/-/g, '')}_to_${formatDate(toDate).replace(/-/g, '')}.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
  };

  
  const headerCell = (align = 'left') => ({
    color: 'white', fontWeight: '600', fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '4px 10px', textAlign: align,
    whiteSpace: 'nowrap', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  });

  const bodyCell = (align = 'left', bold = false) => ({
    fontSize: '12px', padding: '6px 10px',
    color: '#2d3748', whiteSpace: 'nowrap',
    textAlign: align, fontWeight: bold ? '600' : '400',
  });

  const SPECIAL = {
    'Opening Balance': { bg: '#e8f4fd', color: '#1565c0', border: '1px solid #bbdefb' },
    'Closing Balance': { bg: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' },
  };

  const pageStartIndex = (currentPage - 1) * itemsPerPage;

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#f8fafc', padding: '10px', position: 'relative' }}>
        {loading && <LoadingOverlay open={loading} message="Loading Petty Cash Report..." />}

        <div style={{
          margin: '0 auto', background: 'white', borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px',
          color: 'black', fontFamily: 'Times New Roman', overflow: 'hidden',
        }}>
          {/* Title */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase' }}>
              <span style={{ color: 'black', fontFamily: 'Times New Roman' }}>💰 Petty Cash Report</span>
            </h4>
          </div>

          {/* Filters Row */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ border: '1px solid #cbd5e1', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#f8f9fa', color: '#333', outline: 'none', cursor: 'pointer' }} />

            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ border: '1px solid #cbd5e1', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#f8f9fa', color: '#333', outline: 'none', cursor: 'pointer' }} />

            <Button variant="contained" onClick={handleRefresh}
              style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)',
              }}>
              Refresh
            </Button>

            <Button variant="contained" onClick={handleExportExcel} disabled={filteredData.length === 0}
              style={{
                background: filteredData.length === 0 ? '#ccc' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: filteredData.length === 0 ? 'none' : '0 2px 8px rgba(245,158,11,0.4)',
              }}>
              📥 Excel
            </Button>

            <div style={{ flex: 1 }} />

            <TextField size="small" placeholder="Search..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#0f3460', fontSize: '18px' }} /></InputAdornment> }}
              sx={{ minWidth: '180px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#0f3460' }, '&.Mui-focused fieldset': { borderColor: '#0f3460' } } }} />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <style>{`
              .pettycash-scroll::-webkit-scrollbar { height: 10px; width: 8px; }
              .pettycash-scroll::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
              .pettycash-scroll::-webkit-scrollbar-thumb { background: #0f3460; border-radius: 4px; }
              .pettycash-scroll::-webkit-scrollbar-thumb:hover { background: #1a1a2e; }
              .pettycash-scroll { scrollbar-width: thin; scrollbar-color: #0f3460 #f1f5f9; }
            `}</style>
            <div className="pettycash-scroll" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, overflow: 'visible' }}>
                <Table stickyHeader sx={{ minWidth: 700 }} aria-label="petty cash report table">
                  <TableHead>
                    <TableRow style={{ height: '27px' }}>
                      <TableCell style={headerCell()}>BillDate</TableCell>
                      <TableCell style={headerCell()}>BillNo</TableCell>
                      <TableCell style={headerCell()}>Particulars</TableCell>
                      <TableCell style={headerCell('right')}>Receipt</TableCell>
                      <TableCell style={headerCell('right')}>Payment</TableCell>
                      <TableCell style={headerCell('right')}>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                          {loading ? 'Loading...' : 'No records found. Select date range and click Refresh.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {paginatedData.map((item, index) => {
                          const globalIndex = pageStartIndex + index;
                          const particulars = item.Particulars || item.particulars || item.description1 || '';
                          const special = SPECIAL[particulars];
                          const receipt = parseFloat(item.Receipt ?? item.receipt ?? item.debit ?? 0);
                          const payment = parseFloat(item.Payment ?? item.payment ?? item.credit ?? 0);
                          const displayBalance = balances[globalIndex];
                          const rowBg = special ? special.bg : (index % 2 === 0 ? '#ffffff' : '#f9fafb');
                          const textColor = special ? special.color : '#2d3748';
                          return (
                            <TableRow key={item.id || item.Id || globalIndex} style={{ background: rowBg, borderBottom: special ? special.border : '1px solid #f0f0f0' }}>
                              <TableCell style={{ ...bodyCell(), color: textColor, fontWeight: special ? '700' : '400' }}>
                                {formatDate(item.BillDate || item.billDate || item.date1)}
                              </TableCell>
                              <TableCell style={{ ...bodyCell('left', true), color: textColor }}>
                                {item.BillNo ?? item.billNo ?? item.Estimateno ?? '—'}
                              </TableCell>
                              <TableCell style={{ ...bodyCell(), color: textColor, fontWeight: special ? '700' : '400' }}>
                                {particulars || '—'}
                              </TableCell>
                              <TableCell style={{ ...bodyCell('right'), color: textColor, fontWeight: special ? '700' : '400' }}>
                                {receipt > 0 ? fmt(receipt) : '—'}
                              </TableCell>
                              <TableCell style={{ ...bodyCell('right'), color: textColor, fontWeight: special ? '700' : '400' }}>
                                {payment > 0 ? fmt(payment) : '—'}
                              </TableCell>
                              <TableCell style={{ ...bodyCell('right', true), color: special ? special.color : (displayBalance < 0 ? '#c62828' : '#2e7d32') }}>
                                {displayBalance === null ? '—' : (displayBalance !== undefined ? `${fmt(Math.abs(displayBalance))} ${displayBalance < 0 ? 'Cr' : 'Dr'}` : '—')}
                              </TableCell>
                            </TableRow>
                          );
                        })}

                        {/* Closing Balance Row — always shown on last page or single page */}
                        {currentPage === totalPages && (() => {
                          const lastValidBal = [...balances].reverse().find(b => b !== null && b !== undefined);
                          const closingBal = lastValidBal ?? 0;
                          const totalReceipt = filteredData.reduce((sum, item) => {
                            const p = item.Particulars || item.particulars || item.description1 || '';
                            if (p === 'Opening Balance' || p === 'Closing Balance') return sum;
                            return sum + parseFloat(item.Receipt ?? item.receipt ?? item.debit ?? 0);
                          }, 0);
                          const totalPayment = filteredData.reduce((sum, item) => {
                            const p = item.Particulars || item.particulars || item.description1 || '';
                            if (p === 'Opening Balance' || p === 'Closing Balance') return sum;
                            return sum + parseFloat(item.Payment ?? item.payment ?? item.credit ?? 0);
                          }, 0);
                          return (
                            <TableRow style={{ background: SPECIAL['Closing Balance'].bg, borderTop: '2px solid #a5d6a7', borderBottom: SPECIAL['Closing Balance'].border }}>
                              <TableCell style={{ ...bodyCell(), color: SPECIAL['Closing Balance'].color, fontWeight: '700' }}>
                                {formatDate(toDate)}
                              </TableCell>
                              <TableCell style={{ ...bodyCell('left', true), color: SPECIAL['Closing Balance'].color }}>
                                —
                              </TableCell>
                              <TableCell style={{ ...bodyCell(), color: SPECIAL['Closing Balance'].color, fontWeight: '700' }}>
                                Closing Balance
                              </TableCell>
                              <TableCell style={{ ...bodyCell('right', true), color: SPECIAL['Closing Balance'].color }}>
                                {totalReceipt > 0 ? fmt(totalReceipt) : '—'}
                              </TableCell>
                              <TableCell style={{ ...bodyCell('right', true), color: SPECIAL['Closing Balance'].color }}>
                                {totalPayment > 0 ? fmt(totalPayment) : '—'}
                              </TableCell>
                              <TableCell style={{ ...bodyCell('right', true), color: closingBal < 0 ? '#c62828' : SPECIAL['Closing Balance'].color }}>
                                {`${fmt(Math.abs(closingBal))} ${closingBal < 0 ? 'Cr' : 'Dr'}`}
                              </TableCell>
                            </TableRow>
                          );
                        })()}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages}
            totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={n => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[25, 50, 100]}
          />
        </div>
      </div>
    </Fade>
  );
}

export default PettyCashReport;
